import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Edit, Trash2, Calendar as CalendarIcon, X, MessageSquareText, PhoneCall, Globe, CheckCircle2 } from "lucide-react";
import { SortableHead, useSortedRows } from "@/components/SortableHeader";

const EMPTY = { customer_id: "", technician_id: "", status: "", start_date: "", end_date: "", source: "" };

const ACCESSORS = {
  when: (a) => `${a.date} ${a.time}`,
  title: (a) => a.title,
  customer_name: (a) => a.customer_name,
  technician_name: (a) => a.technician_name || "",
  duration: (a) => a.duration,
  status: (a) => a.status,
};

const STATUS = {
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  requested: "bg-amber-100 text-amber-800 border-amber-200",
};

const OPEN = (a) => !["completed", "cancelled", "canceled"].includes(a.status);

export const SmsBadges = ({ a }) => (
  <>
    {a.confirmed_via === "sms" && OPEN(a) && <Badge className="bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-600 gap-1 whitespace-nowrap" title={`Customer replied C on ${a.confirmed_by_sms_at ? new Date(a.confirmed_by_sms_at).toLocaleString("en-US", { timeZone: "America/New_York" }) : ""}`} data-testid="appointment-confirmed-sms-badge"><MessageSquareText className="w-3 h-3" /> Confirmed by text</Badge>}
    {a.confirmed_via === "link" && OPEN(a) && <Badge className="bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-600 gap-1 whitespace-nowrap" title={`Customer tapped the confirm link on ${a.confirmed_by_link_at ? new Date(a.confirmed_by_link_at).toLocaleString("en-US", { timeZone: "America/New_York" }) : ""}`} data-testid="appointment-confirmed-link-badge"><CheckCircle2 className="w-3 h-3" /> Confirmed by link</Badge>}
    {a.source === "online_booking" && <Badge className="bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-100 gap-1 whitespace-nowrap" title="Requested by the customer on the public booking page" data-testid="appointment-booked-online-badge"><Globe className="w-3 h-3" /> Booked online</Badge>}
    {a.reschedule_requested_at && OPEN(a) && <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100 gap-1 whitespace-nowrap" title="Customer replied R - call to agree on a new time" data-testid="appointment-reschedule-badge"><PhoneCall className="w-3 h-3" /> Reschedule requested</Badge>}
  </>
);

const fmtDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

const applyFilters = (appointments, f) =>
  appointments.filter((a) => {
    if (f.customer_id && a.customer_id !== f.customer_id) return false;
    if (f.technician_id && a.technician_id !== f.technician_id) return false;
    if (f.status && a.status !== f.status) return false;
    if (f.source === "online_booking" && a.source !== "online_booking") return false;
    if (f.source === "staff" && a.source === "online_booking") return false;
    if (f.start_date && a.date < f.start_date) return false;
    if (f.end_date && a.date > f.end_date) return false;
    return true;
  });

export const AppointmentTable = ({ appointments, customers, technicians, onEdit, onDelete, onAccept }) => {
  const [filters, setFilters] = useState(EMPTY);
  const set = (patch) => setFilters({ ...filters, ...patch });
  const filtered = useMemo(() => applyFilters(appointments, filters), [appointments, filters]);
  const { sorted, sort, toggle } = useSortedRows(filtered, ACCESSORS, "when", "asc");
  const active = Object.values(filters).some(Boolean);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4">
      <Card className="bg-white border-0 shadow-lg" data-testid="appointment-filters">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <div>
              <Label>Customer</Label>
              <Select value={filters.customer_id || "all"} onValueChange={(v) => set({ customer_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="appointment-filter-customer"><SelectValue placeholder="All Customers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Technician</Label>
              <Select value={filters.technician_id || "all"} onValueChange={(v) => set({ technician_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="appointment-filter-technician"><SelectValue placeholder="All Technicians" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Technicians</SelectItem>
                  {technicians.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={filters.status || "all"} onValueChange={(v) => set({ status: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="appointment-filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="requested">Requested</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Source</Label>
              <Select value={filters.source || "all"} onValueChange={(v) => set({ source: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="appointment-filter-source"><SelectValue placeholder="All Sources" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="online_booking">Booked online</SelectItem>
                  <SelectItem value="staff">Staff / phone agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>From</Label>
              <Input type="date" value={filters.start_date} onChange={(e) => set({ start_date: e.target.value })} data-testid="appointment-filter-start" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={filters.end_date} onChange={(e) => set({ end_date: e.target.value })} data-testid="appointment-filter-end" />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-slate-500" data-testid="appointment-result-count">Showing {filtered.length} of {appointments.length} appointments</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => set({ start_date: today, end_date: today })} data-testid="appointment-filter-today">Today</Button>
              <Button variant="outline" size="sm" onClick={() => set({ start_date: today, end_date: "" })} data-testid="appointment-filter-upcoming">Upcoming</Button>
              {active && <Button variant="ghost" size="sm" onClick={() => setFilters(EMPTY)} data-testid="appointment-filters-clear"><X className="w-4 h-4 mr-1" /> Clear</Button>}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-0 shadow-lg" data-testid="appointment-table-card">
        <CardContent className="p-0 overflow-x-auto">
          <Table data-testid="appointment-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <SortableHead label="Date & Time" sortKey="when" sort={sort} onToggle={toggle} />
                <SortableHead label="Title" sortKey="title" sort={sort} onToggle={toggle} />
                <SortableHead label="Customer" sortKey="customer_name" sort={sort} onToggle={toggle} />
                <SortableHead label="Technician" sortKey="technician_name" sort={sort} onToggle={toggle} />
                <SortableHead label="Duration" sortKey="duration" sort={sort} onToggle={toggle} className="text-right" align="right" />
                <SortableHead label="Status" sortKey="status" sort={sort} onToggle={toggle} />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-16 text-center" data-testid="appointment-table-empty">
                    <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600 font-medium">No appointments match the current filters</p>
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((a) => (
                  <TableRow key={a.id} className={`hover:bg-slate-50 ${a.date === today ? "bg-blue-50/50" : ""}`} data-testid="appointment-row">
                    <TableCell className="whitespace-nowrap">
                      <p className="font-semibold text-slate-800">{fmtDate(a.date)}</p>
                      <p className="text-sm text-slate-500">{a.time}</p>
                    </TableCell>
                    <TableCell className="text-slate-800 max-w-xs"><p className="font-medium truncate" title={a.title}>{a.title}</p>{a.notes && <p className="text-xs text-slate-500 truncate" title={a.notes} data-testid="appointment-notes">{a.notes}</p>}</TableCell>
                    <TableCell className="text-slate-700">{a.customer_name}</TableCell>
                    <TableCell className="text-slate-600">{a.technician_name || <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="appointment-unassigned-badge">UNASSIGNED</Badge>}</TableCell>
                    <TableCell className="text-right text-slate-600 whitespace-nowrap">{a.duration} min</TableCell>
                    <TableCell><div className="flex flex-wrap gap-1"><Badge className={STATUS[a.status] || STATUS.scheduled} data-testid="appointment-status-badge">{a.status.toUpperCase()}</Badge><SmsBadges a={a} /></div></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {onAccept && a.status === "requested" && <Button size="sm" onClick={() => onAccept(a)} className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700 text-white mr-1" title="Accept request and assign a technician" data-testid="accept-appointment-button"><CheckCircle2 className="w-4 h-4 mr-1" /> Accept</Button>}
                      {onEdit && <Button size="sm" variant="ghost" onClick={() => onEdit(a)} className="h-8 px-2 text-blue-600 hover:bg-blue-50" title="Edit" data-testid="edit-appointment-button"><Edit className="w-4 h-4" /></Button>}
                      {onDelete && <Button size="sm" variant="ghost" onClick={() => onDelete(a.id)} className="h-8 px-2 text-red-600 hover:bg-red-50" title="Delete" data-testid="delete-appointment-button"><Trash2 className="w-4 h-4" /></Button>}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
